import { useEffect, useRef } from "react";
import * as THREE from "three";

const INDIA = { lat: 21.6, lon: 78.0 };
const R = 2;

const latLonToVec = (lat, lon, r = R) => {
    const phi = (lat * Math.PI) / 180;
    const lambda = (lon * Math.PI) / 180;
    return new THREE.Vector3(
        r * Math.cos(phi) * Math.sin(lambda),
        r * Math.sin(phi),
        r * Math.cos(phi) * Math.cos(lambda)
    );
};

export const ParticleSphere = ({ className = "" }) => {
    const mountRef = useRef(null);
    const labelRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;
        let disposed = false;
        let cleanup = () => {};

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.1, 100);
        camera.position.z = 6.2;
        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        mount.appendChild(renderer.domElement);

        const globe = new THREE.Group();
        scene.add(globe);

        /* faint inner sphere for depth occlusion feel */
        const core = new THREE.Mesh(
            new THREE.SphereGeometry(R * 0.985, 48, 48),
            new THREE.MeshBasicMaterial({ color: 0x0a0d20, transparent: true, opacity: 0.55 })
        );
        globe.add(core);

        /* India marker */
        const indiaPos = latLonToVec(INDIA.lat, INDIA.lon, R * 1.005);
        const marker = new THREE.Mesh(
            new THREE.SphereGeometry(0.055, 18, 18),
            new THREE.MeshBasicMaterial({ color: 0xf16622 })
        );
        marker.position.copy(indiaPos);
        globe.add(marker);
        const halo = new THREE.Mesh(
            new THREE.RingGeometry(0.09, 0.11, 40),
            new THREE.MeshBasicMaterial({ color: 0xf16622, transparent: true, opacity: 0.7, side: THREE.DoubleSide })
        );
        halo.position.copy(indiaPos);
        halo.lookAt(indiaPos.clone().multiplyScalar(2));
        globe.add(halo);

        /* ambient sparkle */
        const SN = 260;
        const sPos = new Float32Array(SN * 3);
        for (let i = 0; i < SN; i++) {
            const phi = Math.acos(2 * Math.random() - 1);
            const theta = Math.random() * Math.PI * 2;
            const r = R + 0.5 + Math.random() * 1.1;
            sPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
            sPos[i * 3 + 1] = r * Math.cos(phi);
            sPos[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
        }
        const sGeo = new THREE.BufferGeometry();
        sGeo.setAttribute("position", new THREE.BufferAttribute(sPos, 3));
        const sparkles = new THREE.Points(
            sGeo,
            new THREE.PointsMaterial({ color: 0x8fa0ff, size: 0.018, transparent: true, opacity: 0.4 })
        );
        scene.add(sparkles);

        const ring = new THREE.Mesh(
            new THREE.TorusGeometry(2.7, 0.004, 8, 220),
            new THREE.MeshBasicMaterial({ color: 0xf16622, transparent: true, opacity: 0.45 })
        );
        ring.rotation.x = Math.PI / 2.35;
        scene.add(ring);

        /* land dots from real world map */
        fetch("/assets/world-dots.json")
            .then((r) => r.json())
            .then((dots) => {
                if (disposed) return;
                const pos = new Float32Array(dots.length * 3);
                const col = new Float32Array(dots.length * 3);
                const land = new THREE.Color("#cdd6ff");
                const indiaC = new THREE.Color("#ff8a4d");
                dots.forEach(([lat, lon], i) => {
                    const v = latLonToVec(lat, lon);
                    pos[i * 3] = v.x;
                    pos[i * 3 + 1] = v.y;
                    pos[i * 3 + 2] = v.z;
                    const inIndia = lat > 6 && lat < 36 && lon > 68 && lon < 90;
                    const c = inIndia ? indiaC : land;
                    col[i * 3] = c.r;
                    col[i * 3 + 1] = c.g;
                    col[i * 3 + 2] = c.b;
                });
                const geo = new THREE.BufferGeometry();
                geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
                geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
                const pts = new THREE.Points(
                    geo,
                    new THREE.PointsMaterial({ size: 0.024, vertexColors: true, transparent: true, opacity: 0.9 })
                );
                globe.add(pts);
            })
            .catch((e) => console.warn("world-dots.json failed to load", e));

        let mx = 0;
        let my = 0;
        const onMouse = (e) => {
            const rect = mount.getBoundingClientRect();
            mx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
            my = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
        };
        window.addEventListener("mousemove", onMouse);

        const v = new THREE.Vector3();
        let raf;
        const clock = new THREE.Clock();
        const animate = () => {
            const t = clock.getElapsedTime();
            globe.rotation.y = t * 0.07 - (INDIA.lon * Math.PI) / 180 + mx * 0.18;
            globe.rotation.x = my * 0.1;
            sparkles.rotation.y = t * 0.02;
            ring.rotation.z = t * 0.06;
            const pulse = 1 + Math.sin(t * 3) * 0.35;
            halo.scale.setScalar(pulse);
            halo.material.opacity = 0.45 + Math.sin(t * 3) * 0.25;

            /* MS Group label follows the India marker */
            const el = labelRef.current;
            if (el) {
                marker.getWorldPosition(v);
                const facing = v.z > 0.85;
                v.project(camera);
                const x = (v.x * 0.5 + 0.5) * mount.clientWidth;
                const y = (-v.y * 0.5 + 0.5) * mount.clientHeight;
                el.style.transform = `translate(-50%,-50%) translate(${x}px, ${y - 34}px)`;
                el.style.opacity = facing ? "1" : "0";
            }

            renderer.render(scene, camera);
            raf = requestAnimationFrame(animate);
        };
        animate();

        const onResize = () => {
            camera.aspect = mount.clientWidth / mount.clientHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(mount.clientWidth, mount.clientHeight);
        };
        window.addEventListener("resize", onResize);

        cleanup = () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("mousemove", onMouse);
            window.removeEventListener("resize", onResize);
            if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
            sGeo.dispose();
            renderer.dispose();
        };
        return () => {
            disposed = true;
            cleanup();
        };
    }, []);

    return (
        <div className={`relative ${className}`} data-testid="particle-sphere">
            <div ref={mountRef} className="absolute inset-0" />
            <div
                ref={labelRef}
                data-testid="globe-india-label"
                className="pointer-events-none absolute left-0 top-0 flex items-center gap-2 whitespace-nowrap border border-[#F16622]/50 bg-[#0A0D20]/85 px-3 py-1.5 opacity-0 backdrop-blur-md transition-opacity duration-300"
            >
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[#F16622]" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white">
                    MS Group · India
                </span>
            </div>
        </div>
    );
};
